import { useEffect, useRef } from 'react';
import {
  LONG_PRESS_MS,
  reorderColumn,
  pointMovedBeyond,
  findColumnAtX,
} from './headerReorderUtils';

// Header descendants that keep their own native pointer behavior on
// pointerdown: the resize handle (a Box), and any button/input (the grip
// handle, sort toggle, and column-actions menu all render as buttons; the
// filter row renders inputs).
const EXCLUDED_SELECTOR = 'button, input, textarea, .mantine-TableHeadCell-ResizeHandle';

// Blocks the browser's own panning while a column is being dragged.
//
// `preventDefault()` on `pointermove` does NOT do this: per the Pointer
// Events spec a pointer event's default action is decided by
// `touch-action`, so on a touch screen the finger kept dragging the
// column while the table scrolled underneath it. Cancelling `touchmove`
// is what actually stops the pan, and it has to be a NON-PASSIVE
// listener — React attaches touch handlers passively, where
// `preventDefault()` is ignored.
//
// Registered only once the long press has completed, which is also why
// it works: nothing has moved yet, so the browser has not started
// scrolling and the event is still cancelable. Before that point the
// listener is absent and a swipe across the header scrolls the table as
// it always has — the reason this is not simply `touch-action: none` on
// the header, which would cost that scroll for good.
const blockTouchScroll = event => {
  if (event.cancelable) event.preventDefault();
};

// MRT display columns that are never user-reorderable.
const NON_REORDERABLE = new Set([
  'mrt-row-select',
  'mrt-row-actions',
  'mrt-row-expand',
  'mrt-row-drag',
]);

export const useHeaderLongPressReorder = (table, baseHeadCellProps) => {
  const stateRef = useRef(null);      // active gesture
  const justDragged = useRef(false);  // swallow the click after a drag

  // Head cell elements keyed by column id, read from MRT's OWN registry
  // (`table.refs.tableHeadCellRefs`). We deliberately do NOT attach our own
  // `ref` via mantineTableHeadCellProps: that ref would overwrite MRT's on the
  // th (Object.assign order), leaving MRT's `tableHeadCellRefs.current[id]`
  // undefined and crashing the grip-handle drag (`setDragImage(undefined)`).
  const getHeadCells = () => {
    if (table.getHeadCellElements) return table.getHeadCellElements() || {};
    const refs = table.refs && table.refs.tableHeadCellRefs;
    return (refs && refs.current) || {};
  };

  const clearTimer = () => {
    if (stateRef.current && stateRef.current.timer) {
      clearTimeout(stateRef.current.timer);
      stateRef.current.timer = null;
    }
  };

  const reset = () => {
    const s = stateRef.current;
    window.removeEventListener('touchmove', blockTouchScroll);
    if (s && s.el) {
      try {
        s.el.releasePointerCapture(s.pointerId);
      } catch (e) {
        /* pointer already released */
      }
    }
    clearTimer();
    if (s && s.dragging) document.body.style.userSelect = '';
    stateRef.current = null;
  };

  const beginDrag = () => {
    const s = stateRef.current;
    if (!s) return;
    s.dragging = true;
    document.body.style.userSelect = 'none';
    window.addEventListener('touchmove', blockTouchScroll, { passive: false });
    try {
      s.el.setPointerCapture(s.pointerId); // route move/up to the origin cell
    } catch (e) {
      /* element gone */
    }
    if (navigator.vibrate) navigator.vibrate(10);
    table.setDraggingColumn(table.getColumn(s.columnId));
  };

  const onPointerDown = (event, columnId, el) => {
    if (event.button != null && event.button !== 0) return; // primary only
    if (event.target.closest(EXCLUDED_SELECTOR)) return;    // native zones
    reset();
    stateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      columnId,
      el,
      dragging: false,
      timer: setTimeout(beginDrag, LONG_PRESS_MS),
    };
  };

  const onPointerMove = event => {
    const s = stateRef.current;
    if (!s || event.pointerId !== s.pointerId) return;
    if (!s.dragging) {
      // Movement before the hold completes = scroll / text-selection intent.
      if (pointMovedBeyond(s.startX, s.startY, event.clientX, event.clientY)) reset();
      return;
    }
    // Stops the mouse from selecting text across the header; the touch
    // equivalent is the `touchmove` listener added in `beginDrag`.
    event.preventDefault();
    const cells = getHeadCells();
    const rects = [];
    Object.keys(cells).forEach(id => {
      const node = cells[id];
      if (!node) return;
      const r = node.getBoundingClientRect();
      rects.push({ id, left: r.left, right: r.right });
    });
    const targetId = findColumnAtX(rects, event.clientX);
    table.setHoveredColumn(targetId ? table.getColumn(targetId) : null);
  };

  const onPointerUp = event => {
    const s = stateRef.current;
    if (!s || event.pointerId !== s.pointerId) {
      reset();
      return;
    }
    if (s.dragging) {
      const { hoveredColumn, columnOrder } = table.getState();
      if (hoveredColumn && hoveredColumn.id !== s.columnId) {
        table.setColumnOrder(reorderColumn(s.columnId, hoveredColumn.id, columnOrder));
      }
      table.setDraggingColumn(null);
      table.setHoveredColumn(null);
      justDragged.current = true; // a click will fire next; swallow it
    }
    reset();
  };

  const onPointerCancel = () => {
    const s = stateRef.current;
    if (s && s.dragging) {
      table.setDraggingColumn(null);
      table.setHoveredColumn(null);
    }
    reset();
  };

  // A component unmounted mid-drag would otherwise leave the page unable
  // to scroll.
  useEffect(() => () => window.removeEventListener('touchmove', blockTouchScroll), []);

  const onClickCapture = event => {
    if (justDragged.current) {
      justDragged.current = false;
      event.stopPropagation(); // don't let the post-drag click trigger sort
    }
  };

  // Returns the value MRT expects for `mantineTableHeadCellProps`.
  return ({ column }) => {
    const base =
      typeof baseHeadCellProps === 'function'
        ? baseHeadCellProps({ column, table })
        : baseHeadCellProps || {};
    const columnId = column.id;
    const isReorderable =
      !NON_REORDERABLE.has(columnId) && !(column.getIsPinned && column.getIsPinned());
    if (!isReorderable) return base;
    return {
      ...base,
      onPointerDown: event => onPointerDown(event, columnId, event.currentTarget),
      onPointerMove,
      onPointerUp,
      onPointerCancel,
      onClickCapture,
    };
  };
};
